import { getI18n } from "react-i18next";

import { deviceToIconMap } from "@/components/Onboarding/Steps/DeviceType/DeviceType";
import {
  ExtendedSimCardDetail,
  ExtendedSimCardDetailWithEsimInfo,
} from "@/hooks/useExtendedSims";
import {
  ESimInfo,
  ESimInfoStatus,
  GetSimListDevicesItem,
  RoamingInfo,
  RoamingInfoUsageZone,
  SimActionsSimActionsItem,
  SimCardDetail,
  SimCardDetailSimStatus,
  SimCardDetailSimStatusReason,
  WatchSimWatchStatus,
} from "@/services/model";

import { mobileAbos, ServiceIDs } from "./constants";
import { Status } from "./enums";

export const isActive = (device?: SimCardDetail) =>
  device?.simStatus !== SimCardDetailSimStatus.grey;

export const isInactive = (device?: SimCardDetail) =>
  device?.simStatus === SimCardDetailSimStatus.grey;

export const isNumberPortingTile = (device: SimCardDetail) =>
  "numberPorting" in device;

export const canBookWatchSim = (device?: SimCardDetail) =>
  !hasPendingService(device as ExtendedSimCardDetail) &&
  !hasWatchSim(device) &&
  device?.voiceOption;

export const hasSimAutoRenewal = (simCard: SimCardDetail) =>
  simCard.simStatus === SimCardDetailSimStatus.green;

export const hasWatchSim = (device?: SimCardDetail) =>
  device && !!device.watchSim;

export const hasWatchSimAutoRenewal = (simCard: SimCardDetail) =>
  !!simCard.successorForRenew?.description?.includes("Watch");

// Watch SIM status helpers
export const hasWatchSimPending = (simCard: SimCardDetail) =>
  simCard.watchSim?.watchStatus === WatchSimWatchStatus.PENDING;
export const hasWatchSimOrdered = (simCard: SimCardDetail) =>
  simCard.watchSim?.watchStatus === WatchSimWatchStatus.ORDERED;

export const hasSimId = (device: ExtendedSimCardDetail) =>
  device.simId !== undefined;

export const hasEsimInfo = (
  device: ExtendedSimCardDetail,
): device is ExtendedSimCardDetailWithEsimInfo => device.esimInfo !== undefined;

export const hasFlatSpeedPackage = (device: ExtendedSimCardDetail) => {
  return !!device.localFlatSpeed?.length;
};

export const canOrderRoaming = (
  device: ExtendedSimCardDetail | SimCardDetail,
) => {
  return !!device?.simActions?.simActions?.includes(
    SimActionsSimActionsItem.canOrderRoaming,
  );
};

export const hasPendingService = (device: SimCardDetail) =>
  !!device.servicePending;

/**
 * This helper returns true if a customer's identity has been verified, and
 * false otherwise.
 * KYC stands for 'know your customer'.
 */
export const isKYCPendingTile = (
  device: ExtendedSimCardDetail,
  needsIdCheck: boolean | undefined,
) => needsIdCheck && !isNumberPortingTile(device);

// Checks for physical SIMs

export const isPhysicalSIM = (simDetail: SimCardDetail) =>
  simDetail.simType === "Triple SIM";

export const isPendingPhysicalSim = (device: ExtendedSimCardDetail) =>
  hasSimId(device) && isPhysicalSIM(device) && hasPendingService(device);

// Checks for eSIMs

export const isEsim = (sim: Omit<SimCardDetail, "roaming">) =>
  sim.simType === "eSIM";

// Checks if an esim should show the QR code, which is the case for some status
export const isUnscannedEsimInfo = (esimInfo: ESimInfo | undefined) =>
  esimInfo !== undefined &&
  (esimInfo?.status === ESimInfoStatus.RELEASED ||
    esimInfo?.status === ESimInfoStatus.INSTALLED ||
    esimInfo?.status === ESimInfoStatus.ERROR_INSTALL_FAILED);

export const isEsimchangeInProgress = (
  esimInfo: ESimInfo[] | undefined,
): boolean => !!esimInfo?.[0]?.esimchangeInProgress;

export const isEsimresetInProgress = (
  esimInfo: ESimInfo[] | undefined,
): boolean => !!esimInfo?.[0]?.esimresetInProgress;

export const isPendingEsim = (device: ExtendedSimCardDetail) =>
  isEsim(device) && hasPendingService(device);

export const needsQRCodeTile = (device: ExtendedSimCardDetail) =>
  hasSimId(device) && isEsim(device) && isUnscannedEsimInfo(device.esimInfo);

export const isEsimQRInProgress = (device: ExtendedSimCardDetail) => {
  return device.esimInfo?.esimchangeInProgress === true;
};

// Utilitiy functions

export const iccidWithSpaces = (x = "") => {
  const iccid = x.match(/^(\d{6})(\d{6})(\d{6})(\d{2})/);
  if (iccid && iccid.length >= 4) {
    return `${iccid[1]} ${iccid[2]} ${iccid[3]} ${iccid[4]}`;
  } else {
    return x;
  }
};

export const toPhoneNumber = (phoneNumber: string) => {
  return phoneNumber.replace(/^0(.*)$/, "+41 $1");
};

/** Use successor product label when the active package was cleared but renewal is configured. */
export const shouldUseSuccessorForProductDisplay = (simDetail: SimCardDetail) =>
  isInactive(simDetail) ||
  !!simDetail.servicePending ||
  simDetail.simStatusReason === SimCardDetailSimStatusReason.LOCK_REQUESTED;

/** Active package label, or planned renewal product when applicable. */
export const getProductDescriptionSource = (simDetail: SimCardDetail) =>
  simDetail.localFlatSpeed ||
  (shouldUseSuccessorForProductDisplay(simDetail)
    ? simDetail.successorForRenew?.description
    : undefined);

export const hasDisplayableProductDescription = (simDetail: SimCardDetail) =>
  !!getProductDescriptionSource(simDetail);

export const getSpeedFromLocalFlatSpeed = (simDetail?: SimCardDetail) => {
  if (!simDetail) return "-";

  const serviceID = simDetail.serviceID ?? simDetail.successorForRenew?.id;
  const productDescription = getProductDescriptionSource(simDetail);

  // For mobile abos, always use standardized speed values for consistency
  if (serviceID !== undefined && mobileAbos.includes(`${serviceID}`)) {
    const tariffType = getTariffType(serviceID);

    switch (tariffType) {
      case TariffTypes.flatMobile:
      case TariffTypes.flatMobilePlus:
      case TariffTypes.flatMobileSwiss:
        return "2000 Mbit/s";
      default:
        return "30 Mbit/s";
    }
  }

  // Use displaySpeed if available (new parameter from API) for non-mobile-abo tariffs
  if (simDetail.displaySpeed) {
    return simDetail.displaySpeed;
  } else {
    if (!productDescription) return "-";

    let speedMatch = productDescription.match(/^\w+ ([0-9.]*) |/);
    let speed = 0;
    if (!speedMatch?.[1]) {
      speedMatch = productDescription.match(/^[0-9]+/);
      speed = Number(speedMatch?.[0]) || 0;
    } else {
      speed = Number(speedMatch && speedMatch[1]) || 0;
    }
    if (speed >= 1000) {
      return `${speed / 1000} Gbit/s`;
    } else {
      return `${speed} Mbit/s`;
    }
  }
};

export const stripFlatName = (fullName = "") =>
  fullName.replace(/ [|+] .*/, "");

export const getBaseFlatName = (device: SimCardDetail) =>
  stripFlatName(device.localFlatSpeed || "");

export const getFlatNameFromDevice = (
  device: SimCardDetail,
  isCockpit: boolean = true,
) => {
  const productDescription = getProductDescriptionSource(device) ?? "";
  const serviceID = device?.serviceID ?? device?.successorForRenew?.id ?? 0;

  if (mobileAbos.includes(serviceID.toString())) {
    if (device.watchSim) {
      return `${stripFlatName(getFlatNameFromLocalFlatSpeed(productDescription))} ${isCockpit ? "+" : "|"} ${isCockpit ? "Watch" : "Watch SIM"}`;
    }

    return getFlatNameFromLocalFlatSpeed(productDescription);
  }

  return getFlatNameFromLocalFlatSpeed(productDescription);
};

export const getFlatNameFromLocalFlatSpeed = (value = "") =>
  stripFlatName(value) +
  (value.includes("Voice") ? " + Voice" : "") +
  (value.includes("Watch") ? " + Watch" : "");

export const getExtendedFlatNameFromLocalFlatSpeed = (value = "") =>
  stripFlatName(value) +
  (value.includes("Voice") ? " | Voice Option" : "") +
  (value.includes("Watch") ? " | Watch SIM" : "");

export const getTariffName = (simDetail: SimCardDetail) => {
  const { t } = getI18n();

  if (!hasDisplayableProductDescription(simDetail)) {
    return isInactive(simDetail)
      ? t("portal:layout.tiles.inactive.noPackageActive")
      : simDetail.servicePending
        ? "-"
        : getFlatNameFromDevice(simDetail);
  }

  return getFlatNameFromDevice(simDetail);
};

export enum TariffTypes {
  "flatMobile",
  "flatMobilePlus",
  "flatMobileSwiss",
  "flatHome4G",
  "flatHome5G",
  "dataTariff",
}

export const getTariffType = (serviceID?: number) => {
  switch (serviceID) {
    case ServiceIDs.FlatMobileMonth:
    case ServiceIDs.FlatMobileYear:
      return TariffTypes.flatMobile;

    case ServiceIDs.FlatMobilePlusMonth:
    case ServiceIDs.FlatMobilePlusYear:
      return TariffTypes.flatMobilePlus;

    case ServiceIDs.FlatMobileSwissMonth:
    case ServiceIDs.FlatMobileSwissYear:
      return TariffTypes.flatMobileSwiss;

    case ServiceIDs.FlatHome4G:
      return TariffTypes.flatHome4G;

    case ServiceIDs.FlatHome5G:
      return TariffTypes.flatHome5G;

    default:
      return TariffTypes.dataTariff;
  }
};

export const sortRoamingOnHierarchy = (a: RoamingInfo, b: RoamingInfo) => {
  if (a.roamingStatus === Status.active || b.roamingStatus === Status.active) {
    if (
      a.roamingStatus === Status.active &&
      b.roamingStatus === Status.active
    ) {
      if (a.usageZone === "ZONE1" && b.usageZone === "ZONE2") return -1;
      if (a.usageZone === "ZONE2" && b.usageZone === "ZONE3") return -1;
      if (a.usageZone === "ZONE2" && b.usageZone === "ZONE1") return 1;
      if (a.usageZone === "ZONE3" && b.usageZone === "ZONE2") return 1;
    } else {
      if (a.roamingStatus === Status.active) return -1;
      if (b.roamingStatus === Status.active) return 1;
    }
  } else {
    if ((a.remainingDays || 0) < (b.remainingDays || 0)) {
      return -1;
    }
  }

  return 0;
};

type roamingTransformerType = (
  device: ExtendedSimCardDetail,
) => RoamingInfo | undefined;
export const getFirstOfSortedRoaming: roamingTransformerType = (device) =>
  device.roamingInfo?.sort(sortRoamingOnHierarchy)[0];

/**
 * Checks if a roaming zone is Zone 1 (EU/USA/UK)
 */
export const isZone1 = (
  zone: RoamingInfoUsageZone | string | undefined,
): boolean => zone === RoamingInfoUsageZone.ZONE1;

/**
 * Converts MB to GB and truncates to 2 decimals (no rounding up).
 * Example: 1387 MB -> "1.38"
 */
export const formatRoamingUsageInGb = (volumeInMB: number): string => {
  const truncatedHundredths = Math.trunc((volumeInMB / 1000) * 100);
  const value = truncatedHundredths / 100;

  return parseFloat(value.toFixed(2)).toString();
};

/**
 * Converts MB to GB for total package display.
 * - For values below 1 GB, returns up to 2 decimals (truncated).
 * - For values >= 1 GB, rounds to whole GB (half up).
 * - Special case: keep 24 GB for the known 24 GB package base volume (24576 MB).
 *   This preserves the marketed 24 GB display for FM Plus.
 */
export const formatRoamingTotalInGb = (volumeInMB: number): string =>
  volumeInMB < 1000
    ? formatRoamingUsageInGb(volumeInMB)
    : volumeInMB === 24576
      ? "24"
      : Math.round(volumeInMB / 1000).toString();

/**
 * Formats roaming volume for display.
 * Zone 1 (EU/USA/UK): displays in GB
 * Zone 2 and Zone 3: displays in MB
 *
 * @param volumeInMB - The volume in megabytes
 * @param zone - The roaming zone (ZONE1, ZONE2, ZONE3)
 * @returns Formatted string with unit (e.g., "5 GB" or "500 MB")
 */
export const formatRoamingVolume = (
  volumeInMB: number | undefined,
  zone: RoamingInfoUsageZone | string | undefined,
): string => {
  if (volumeInMB === undefined || volumeInMB === null) return "-";

  if (isZone1(zone)) {
    return `${formatRoamingTotalInGb(volumeInMB)} GB`;
  }

  // Zone 2 and Zone 3 remain in MB
  return `${volumeInMB} MB`;
};

export const hasDifferentSuccessor = (
  simDetail: SimCardDetail | undefined,
): boolean => {
  return (
    simDetail?.successorForRenew !== undefined &&
    simDetail.successorForRenew.id !== simDetail.serviceID
  );
};

export const devices = [
  "Smartphone",
  "Tablet",
  "Laptop",
  "WLAN Home Router",
  "Mobile Hotspot",
  "GPS Tracker",
  "Wearable",
  "Kamera",
  "IoT Gerät",
  "Fahrzeug",
  "Andere",
] as const;

export type DeviceName = (typeof devices)[number];

// Map name to icon
export const getIconForDevice = (description?: string) =>
  deviceToIconMap[getDeviceDescription(description)];

export const getDeviceDescription: (device?: string) => DeviceName = (
  device,
) =>
  device !== undefined && devices.includes(device as DeviceName)
    ? (device as DeviceName)
    : "Andere";

export const getIconForDescription = (device?: string) =>
  getIconForDevice(getDeviceDescription(device));

export const hasAllowedManagement = (device: SimCardDetail) =>
  device.simStatus !== SimCardDetailSimStatus.red;

export const checkIfDeviceCanReloadEsim = (esimInfo: ESimInfo[]) =>
  esimInfo?.[0]?.esimresetAllowed;

export const checkIfDeviceCanTransferEsim = (esimInfo: ESimInfo[]) =>
  esimInfo?.[0]?.esimTransferAllowed;

export const deviceNameToValueMap: Record<DeviceName, GetSimListDevicesItem> = {
  Smartphone: GetSimListDevicesItem.Smartphone,
  Tablet: GetSimListDevicesItem.Tablet,
  Laptop: GetSimListDevicesItem.Laptop,
  "WLAN Home Router": GetSimListDevicesItem.Router,
  "Mobile Hotspot": GetSimListDevicesItem.Mobile_Hotspot,
  "GPS Tracker": GetSimListDevicesItem.GPS_Tracker,
  Wearable: GetSimListDevicesItem.Wearable,
  Kamera: GetSimListDevicesItem.Camera,
  "IoT Gerät": GetSimListDevicesItem.IoT_Device,
  Fahrzeug: GetSimListDevicesItem.Vehicle,
  Andere: GetSimListDevicesItem.Andere,
} as const;

export const deviceValueToNameMap: Record<GetSimListDevicesItem, DeviceName> = {
  [GetSimListDevicesItem.Smartphone]: "Smartphone",
  [GetSimListDevicesItem.Tablet]: "Tablet",
  [GetSimListDevicesItem.Laptop]: "Laptop",
  [GetSimListDevicesItem.Router]: "WLAN Home Router",
  [GetSimListDevicesItem.Mobile_Hotspot]: "Mobile Hotspot",
  [GetSimListDevicesItem.GPS_Tracker]: "GPS Tracker",
  [GetSimListDevicesItem.Wearable]: "Wearable",
  [GetSimListDevicesItem.Camera]: "Kamera",
  [GetSimListDevicesItem.IoT_Device]: "IoT Gerät",
  [GetSimListDevicesItem.Vehicle]: "Fahrzeug",
  [GetSimListDevicesItem.Andere]: "Andere",
} as const;

export const hasNumberPorting = (device: SimCardDetail) =>
  device.simStatusReason === SimCardDetailSimStatusReason.WF_ACTIVE_NP;

export const canBeChangedToFlatMobile = (device: SimCardDetail) =>
  !hasPendingService(device) &&
  device.simStatus !== SimCardDetailSimStatus.red &&
  !hasNumberPorting(device);
